import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { GetBotResponse } from 'src/app/models/bot.interface';
import {
  InstagramAccount,
  InstagramBot,
  InstagramCommentStats,
  InstagramRecentComment,
  InstagramCommentsResponse,
  MessengerBot,
  PageResponse,
} from 'src/app/models/messenger-bot.interface';
import { MessengerBotService } from 'src/app/core/services/messenger-bot.service';
import { DataService } from '../../data-service.service';
import { environment } from 'src/environments/environment';
import { InstagramBotService } from 'src/app/core/services/instagram-bot.service';
import { AttentionScheduleConfig, UpdateAttentionSchedulePayload } from 'src/app/models/attention-schedule.interface';
import { DashboardService } from 'src/app/core/services/dashboard.service';
import { Subject, Subscription, firstValueFrom, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';

declare const FB: any;
@Component({
  selector: 'globot-instagram-bot',
  templateUrl: './instagram-bot.component.html',
  styleUrls: ['./instagram-bot.component.scss'],
})
export class InstagramBotComponent implements OnInit, OnChanges, OnDestroy {
  @Input() bot?: GetBotResponse;
  @Input() activeTab: string = 'integration';

  /** Interruptor del canal: solo con cuenta Instagram vinculada. */
  get showInstagramChannelSwitch(): boolean {
    return !!this.instagramBot;
  }

  /** Configuración de comentarios: solo con cuenta Instagram vinculada. */
  get showInstagramCommentsConfig(): boolean {
    return this.showInstagramChannelSwitch;
  }

  private destroy$ = new Subject<void>();
  private destroyed = false;
  private subscriptionMessenger!: Subscription;
  pageSubscribed?: MessengerBot;
  instagramBot?: InstagramBot;
  botEnabled: boolean = true;
  commentsReplyEnabled: boolean = false;
  commentsPredefinedMessage: string = '';
  commentsTriggerType: 'any' | 'keywords' = 'any';
  commentsKeywordsArray: string[] = [];
  commentsReplySendDm: boolean = true;
  commentsReplySendPublic: boolean = false;
  commentsPredefinedMessagePublic: string = '';
  savingCommentsConfig: boolean = false;
  /** Evita que el switch se monte con false antes de cargar el GET. */
  commentsConfigLoaded: boolean = false;
  /** Evita el flash de los botones de conexión antes de cargar el GET. */
  instagramBotLoaded: boolean = false;
  private instagramBotRequestSeq = 0;
  readonly commentsTriggerOptions = [
    { label: 'Todos los comentarios', value: 'any' as const },
    { label: 'Palabras clave', value: 'keywords' as const },
  ];
  recentComments: InstagramRecentComment[] = [];
  hasMoreComments: boolean = false;
  loadingComments: boolean = false;
  commentStats?: InstagramCommentStats;
  loadingStats: boolean = false;
  igDmStats?: { ig_count: number; ig_messages_count: number };
  loadingIgDmStats: boolean = false;

  commentsDialogVisible: boolean = false;
  dialogComments: InstagramRecentComment[] = [];
  dialogPage: number = 1;
  dialogPageSize: number = 10;
  dialogTotal: number = 0;
  dialogLoading: boolean = false;
  loading: boolean = false;
  refreshingToken: boolean = false;
  refreshing: boolean = false;
  facebookApiVersion = environment.facebookApiVersion;
  facebookAppId = environment.facebookAppId;
  instagramConfigId = environment.instagramConfigId;
  dialogVisible = false;
  deleteDialogVisible = false;
  notFoundedAccountDialog = false;
  instagramDetected?: InstagramAccount;
  facebookAppSecret = environment.facebookAppSecret;
  constructor(
    private messengerBotService: MessengerBotService,
    private instagramBotService: InstagramBotService,
    private dashboardService: DashboardService,
    private ms: MessageService,
    private dataService: DataService,
    private changeDetectorRef: ChangeDetectorRef,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  get showTokenAlert(): boolean {
    return (
      this.instagramBot?.auth_type === 'instagram' &&
      ['expiring_soon', 'expired'].includes(this.instagramBot?.token_status ?? '')
    );
  }

  get tokenDaysLeft(): number {
    if (!this.instagramBot?.token_expires_at) return 0;
    const diff = new Date(this.instagramBot.token_expires_at).getTime() - Date.now();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }

  get tokenExpiryPercent(): number {
    const days = this.tokenDaysLeft;
    return Math.min(100, Math.round((days / 7) * 100));
  }

  ngOnChanges(changes: SimpleChanges): void {
    const botChange = changes['bot'];
    if (botChange && !botChange.isFirstChange() && botChange.currentValue?.id) {
      this.getInstagramBot();
      this.getMessengerBot().then((response) => {
        if (response.length > 0) {
          this.pageSubscribed = response[0];
        }
      });
    }
  }

  async ngOnInit(): Promise<void> {
    FB.init(
      {
        appId: this.facebookAppId,
        cookie: true,
        xfbml: true,
        version: this.facebookApiVersion,
      },
      {
        config_id: this.instagramConfigId,
      }
    );
    this.subscriptionMessenger = this.dataService
      .getMessengerStatus()
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.pageSubscribed = value.messengerBot;
        this.getInstagramBot();
        this.changeDetectorRef.detectChanges();
      });
    const response = await this.getMessengerBot();
    if (response.length > 0) {
      this.pageSubscribed = response[0];
    }

    await this.getInstagramBot();
    this._handleOAuthCallback();
  }
  ngOnDestroy() {
    this.destroyed = true;
    this.subscriptionMessenger.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private _handleOAuthCallback(): void {
    const params = this.route.snapshot.queryParamMap;
    const connected = params.get('connected');
    const error = params.get('error');

    if (connected === 'true') {
      this.showMessage(
        'success',
        'Instagram conectado',
        'Tu cuenta de Instagram se conectó correctamente'
      );
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
      return;
    }
    if (error) {
      const messages: Record<string, string> = {
        user_denied: 'Cancelaste la autorización de Instagram',
        invalid_state: 'La sesión expiró. Vuelve a intentarlo',
        token_exchange_failed: 'No se pudo obtener el token de Instagram. Intenta de nuevo',
        profile_fetch_failed: 'No se pudo obtener el perfil de Instagram',
        already_connected: 'Esta cuenta de Instagram ya está conectada a otro asistente',
        permission_denied: 'No tienes permiso para conectar canales.',
        no_chatbot: 'No se pudo verificar el acceso a este asistente',
      };
      const detail = messages[error] ?? 'Ocurrió un error al conectar Instagram';
      this.showMessage('error', 'Error al conectar Instagram', detail);
      this.router.navigate([], { queryParams: {}, replaceUrl: true });
    }
  }

  linkInstagramAccount(): void {
    if (!this.bot?.id) return;
    this.instagramBotService.initiateInstagramOAuth(this.bot.id, window.location.href);
  }

  refreshInstagramTokenHandler(): void {
    if (!this.bot?.id || this.refreshingToken) return;
    this.refreshingToken = true;
    this.instagramBotService
      .refreshInstagramToken(this.bot.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.showMessage(
            'success',
            'Token renovado',
            'La conexión con Instagram se renovó correctamente'
          );
          this.getInstagramBot();
          this.refreshingToken = false;
        },
        error: () => {
          this.showMessage(
            'error',
            'Error al renovar',
            'No se pudo renovar el token. Reconecta la cuenta manualmente'
          );
          this.refreshingToken = false;
        },
      });
  }

  showMessage(severity: string, summary: string, detail: string) {
    this.ms.add({ key: 'tst', severity: severity, summary: summary, detail: detail });
  }

  showDialog(value: boolean) {
    this.dialogVisible = value;
  }

  showDialogDelete(value: boolean) {
    this.deleteDialogVisible = value;
  }

  showDialogNotFoundedAccount(value: boolean) {
    this.notFoundedAccountDialog = value;
  }

  closeDialog(_event: any) {
    this.loading = false;
    this.showDialog(false);
  }

  closeDialogDelete(_event: any) {
    this.loading = false;
    this.showDialogDelete(false);
  }

  closeDialogNotFoundedAccount(_event: any) {
    this.loading = false;
    this.showDialogNotFoundedAccount(false);
  }

  async detectInstagramAccount() {
    this.loading = true;
    const response = await this.getMessengerBot();
    if (response.length < 1) {
      this.loading = false;
      this.linkFacebookAccount();
      return;
    }
    const messengerBot = response[0];
    let page = await this.getPage(messengerBot.page_id, messengerBot.page_access_token);

    if (!page) {
      this.loading = false;
      this.showMessage('error', 'Acción fallida', 'No se encontraron datos de facebook');
      return;
    }
    if (!page.instagram_business_account) {
      this.loading = false;
      this.showDialogNotFoundedAccount(true);
      return;
    }
    this.pageSubscribed = messengerBot;
    this.instagramDetected = page.instagram_business_account;
    this.showDialog(true);
  }

  subscribeIgAccount(): void {
    this.showDialog(false);

    this.instagramBotService
      .postAccountSubscription(
        this.bot!.id,
        this.instagramDetected!,
        this.pageSubscribed!.id,
        this.pageSubscribed!.page_access_token
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.showMessage('success', 'Acción exitosa', 'Tu asistente se conectó con Instagram');
          this.getInstagramBot();
        },
        error: (_err) => {
          this.loading = false;
          this.showMessage(
            'error',
            'Acción fallida',
            'No se pudo suscribir la cuenta de instagram'
          );
        },
      });
  }

  async getInstagramBot() {
    if (!this.bot?.id) {
      return;
    }
    const requestSeq = ++this.instagramBotRequestSeq;
    this.instagramBotService
      .getBotInstagram(this.bot.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          if (requestSeq !== this.instagramBotRequestSeq) {
            return;
          }
          this.setStatus(response.status!, response.data);
          if (response.data) {
            this.loadRecentComments();
            this.loadCommentStats();
            this.loadIgDmStats();
          }
        },
        error: (_err) => {
          if (requestSeq !== this.instagramBotRequestSeq) {
            return;
          }
          this.setStatus('NOT_FOUNDED', undefined);
        },
      });
  }

  get commentStatsDmSent(): number {
    if (!this.commentStats) return 0;
    return this.commentStats.replied.dm_only + this.commentStats.replied.both;
  }

  get commentStatsPublicSent(): number {
    if (!this.commentStats) return 0;
    return this.commentStats.replied.public_only + this.commentStats.replied.both;
  }

  private todayString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
      now.getDate()
    ).padStart(2, '0')}`;
  }

  private firstDayOfMonthString(): string {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }

  loadCommentStats() {
    if (!this.bot?.id) return;
    this.loadingStats = true;
    this.instagramBotService
      .getCommentStats(this.bot.id, this.firstDayOfMonthString(), this.todayString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.commentStats = response;
          this.loadingStats = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.commentStats = undefined;
          this.loadingStats = false;
        },
      });
  }

  loadIgDmStats() {
    if (!this.bot?.id) return;
    this.loadingIgDmStats = true;
    this.instagramBotService
      .getIgDmStats(this.bot.id, this.firstDayOfMonthString(), this.todayString())
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.igDmStats = {
            ig_count: response.ig_count ?? 0,
            ig_messages_count: response.ig_messages_count ?? 0,
          };
          this.loadingIgDmStats = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.igDmStats = undefined;
          this.loadingIgDmStats = false;
        },
      });
  }

  loadRecentComments() {
    if (!this.bot?.id) return;
    this.loadingComments = true;
    this.instagramBotService
      .getRecentComments(this.bot.id, 1, 6)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: InstagramCommentsResponse) => {
          const all = response.data ?? [];
          this.hasMoreComments = all.length > 5 || response.total > 5;
          this.recentComments = all.slice(0, 5);
          this.loadingComments = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.recentComments = [];
          this.hasMoreComments = false;
          this.loadingComments = false;
        },
      });
  }

  openCommentsDialog() {
    this.commentsDialogVisible = true;
    this.dialogPage = 1;
    this.loadDialogPage(1);
  }

  closeCommentsDialog() {
    this.commentsDialogVisible = false;
  }

  loadDialogPage(page: number) {
    if (!this.bot?.id) return;
    this.dialogLoading = true;
    this.dialogPage = page;
    this.instagramBotService
      .getRecentComments(this.bot.id, page, this.dialogPageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: InstagramCommentsResponse) => {
          this.dialogComments = response.data ?? [];
          this.dialogTotal = response.total;
          this.dialogLoading = false;
          this.changeDetectorRef.detectChanges();
        },
        error: () => {
          this.dialogComments = [];
          this.dialogLoading = false;
        },
      });
  }

  get dialogTotalPages(): number {
    return Math.ceil(this.dialogTotal / this.dialogPageSize);
  }

  trackByCommentId(_index: number, comment: InstagramRecentComment): string {
    return comment.comment_id;
  }

  timeAgo(isoDate: string): string {
    const diff = Date.now() - new Date(isoDate).getTime();
    const minutes = Math.floor(diff / 60000);
    if (minutes < 1) return 'hace un momento';
    if (minutes < 60) return `hace ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `hace ${hours} h`;
    const days = Math.floor(hours / 24);
    return `hace ${days} día${days > 1 ? 's' : ''}`;
  }

  attentionScheduleConfig: AttentionScheduleConfig | null = null;
  savingAttentionSchedule = false;

  private applyAttentionScheduleFromBot(data: InstagramBot): void {
    this.attentionScheduleConfig = {
      attention_schedule_enabled: !!data.attention_schedule_enabled,
      attention_schedule_start_time: data.attention_schedule_start_time ?? null,
      attention_schedule_end_time: data.attention_schedule_end_time ?? null,
      away_message_enabled: !!data.away_message_enabled,
      away_message_text: data.away_message_text ?? null,
    };
  }

  saveAttentionSchedule(payload: UpdateAttentionSchedulePayload): void {
    if (!this.bot?.id) {
      return;
    }
    const previousConfig = this.attentionScheduleConfig;
    this.savingAttentionSchedule = true;
    this.instagramBotService
      .updateAccountSubscription(this.bot.id, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.attentionScheduleConfig = {
            ...this.attentionScheduleConfig,
            ...payload,
          } as AttentionScheduleConfig;
          this.savingAttentionSchedule = false;
          this.showMessage('success', 'Acción exitosa', 'Horario de atención actualizado');
        },
        error: (err: unknown) => {
          this.savingAttentionSchedule = false;
          this.attentionScheduleConfig = { ...previousConfig } as AttentionScheduleConfig;
          let detail: string | undefined;
          if (err instanceof HttpErrorResponse) {
            const body = err.error;
            if (body && typeof body === 'object' && 'message' in body) {
              detail = String((body as { message: unknown }).message);
            }
          }
          this.showMessage('error', 'Error al guardar', detail ?? 'No se pudo guardar el horario de atención.');
        },
      });
  }

  private applyCommentsConfigFromBot(data: InstagramBot): void {
    this.commentsReplyEnabled = !!data.comments_reply_enabled;
    this.commentsPredefinedMessage = data.comments_predefined_message ?? '';
    this.commentsTriggerType = data.comments_trigger_type === 'keywords' ? 'keywords' : 'any';
    this.commentsKeywordsArray = data.comments_trigger_keywords ?? [];
    this.commentsReplySendDm = data.comments_reply_send_dm !== false;
    this.commentsReplySendPublic = !!data.comments_reply_send_public;
    this.commentsPredefinedMessagePublic = data.comments_predefined_message_public ?? '';
  }

  private resetCommentsConfig(): void {
    this.commentsReplyEnabled = false;
    this.commentsPredefinedMessage = '';
    this.commentsTriggerType = 'any';
    this.commentsKeywordsArray = [];
    this.commentsReplySendDm = true;
    this.commentsReplySendPublic = false;
    this.commentsPredefinedMessagePublic = '';
  }

  setStatus(status: string, data: InstagramBot | undefined) {
    this.instagramBot = data;
    if (data) {
      this.applyCommentsConfigFromBot(data);
      this.applyAttentionScheduleFromBot(data);
    }

    switch (status) {
      case 'ENABLED':
        this.dataService.setInstagramStatus({
          status: 'Activo',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#BBF7D0',
            color: '#16A34A',
          },
        });
        this.botEnabled = true;
        break;
      case 'DISABLED':
        this.dataService.setInstagramStatus({
          status: 'Inactivo',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#FFF9CE',
            color: '#E0A53E',
          },
        });
        this.botEnabled = false;
        break;
      default:
        this.dataService.setInstagramStatus({
          status: 'Sin configurar',
          style: {
            'padding-left': '10px',
            'padding-right': '10px',
            background: '#EDEDED',
            color: '#6B6B6B',
          },
        });
        this.botEnabled = false;
        this.instagramBot = undefined;
        this.resetCommentsConfig();
        this.attentionScheduleConfig = null;
        break;
    }
    this.commentsConfigLoaded = true;
    this.instagramBotLoaded = true;
    this.changeDetectorRef.detectChanges();
  }

  async getMessengerBot(): Promise<MessengerBot[]> {
    if (!this.bot?.id) {
      return [];
    }
    return firstValueFrom(
      this.messengerBotService.getBotMessenger(this.bot.id).pipe(
        takeUntil(this.destroy$),
        map((response) => response.data),
        catchError(() => of([] as MessengerBot[]))
      ),
      { defaultValue: [] as MessengerBot[] }
    );
  }

  unlinkInstagramAccount() {
    this.loading = true;
    this.showDialogDelete(true);
  }

  async refreshInstagramProfile() {
    if (!this.pageSubscribed || !this.bot?.id) return;
    this.refreshing = true;
    try {
      const page = await this.getPage(
        this.pageSubscribed.page_id,
        this.pageSubscribed.page_access_token
      );
      if (!page?.instagram_business_account) {
        this.showMessage('warn', 'Sin datos', 'No se encontró la cuenta de Instagram vinculada');
        return;
      }
      const ig = page.instagram_business_account;
      await firstValueFrom(
        this.instagramBotService
          .updateAccountSubscription(this.bot.id, {
            profile_picture_url: ig.profile_picture_url,
            account_username: ig.username,
            account_name: ig.name,
            followers_count: ig.followers_count ?? null,
            follows_count: ig.follows_count ?? null,
            media_count: ig.media_count ?? null,
          })
          .pipe(takeUntil(this.destroy$))
      );
      await this.getInstagramBot();
      this.showMessage(
        'success',
        'Perfil actualizado',
        'Los datos de la cuenta de Instagram se actualizaron correctamente'
      );
    } catch {
      // El componente se destruyó a mitad de la petición (takeUntil cortó el observable):
      // no es un error real, así que no mostramos un toast de fallo.
      if (this.destroyed) return;
      this.showMessage('error', 'Acción fallida', 'No se pudo actualizar el perfil de Instagram');
    } finally {
      this.refreshing = false;
    }
  }

  deleteAccountSubscription(): void {
    this.showDialogDelete(false);

    this.instagramBotService
      .deleteBotInstagram(this.bot!.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.loading = false;
          this.showMessage(
            'success',
            'Acción exitosa',
            'Tu asistente se desconectó de Instagram'
          );
          this.getInstagramBot();
        },
        error: (_err) => {
          this.loading = false;
          this.showMessage(
            'error',
            'Acción fallida',
            'No se pudo desconectar la cuenta de instagram'
          );
        },
      });
  }

  async linkFacebookAccount() {
    this.loading = true;
    const response = await this.facebookLogin();
    if (!response.authResponse) {
      this.loading = false;
      return;
    }
    const accessToken = await this.generateAccessToken(response.authResponse.code);
    if (accessToken) {
      await this.handleAccessTokenFlow(accessToken);
    }
  }

  private showErrorAndResetLoading(summary: string, detail: string): void {
    this.ms.add({ key: 'tst', severity: 'error', summary, detail });
    this.loading = false;
  }

  private async handleAccessTokenFlow(userAccessToken: string): Promise<void> {
    const pages = await this.getPages(userAccessToken);
    if (!pages) {
      this.showErrorAndResetLoading(
        'Acción fallida',
        'No se pudo  obtener la página permitida por el usuario.'
      );
      return;
    }
    const userData = await this.getUserData(userAccessToken);
    const userId = userData.id;
    if (pages.length !== 1) {
      this.showErrorAndResetLoading(
        'Acción fallida',
        'Porfavor, selecciona solo una  página para vincular.'
      );
      this.changeDetectorRef.detectChanges();
      await this.revokePermissions(userId, userAccessToken);
      this.facebookLogout();
      return;
    }
    for await (const page of pages) {
      await this.processPageWithAccessToken(page, userId, userAccessToken);
    }
  }

  private async processPageWithAccessToken(
    page: any,
    userId: string,
    userAccessToken: string
  ): Promise<void> {
    if (!page.instagram_business_account) {
      await this.revokePermissions(userId, userAccessToken);
      this.facebookLogout();
      this.showDialogNotFoundedAccount(true);
      this.loading = false;
      return;
    }
    const success = await this.subscribePage(page.id, page.access_token);
    if (!success) {
      this.showMessage(
        'error',
        'Acción fallida',
        'No se pudo suscribir la página ' + page.name + ' porfavor  vuelva a intentarlo.'
      );
      this.loading = false;
      return;
    }
    const categories = page?.category_list?.map((c: any) => c.name) ?? [];
    this.messengerBotService
      .postPageSubscription(this.bot!.id, page.id, false, page.name, categories, page.access_token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.dataService.setRefreshMessenger(true);
          this.loading = false;
          this.showMessage('success', 'Acción exitosa', page.name + ' vinculada con éxito!');
          this.detectInstagramAccount();
        },
        error: () => {
          this.loading = false;
        },
      });
  }
  facebookLogout() {
    FB.getLoginStatus(function (response: any) {
      if (response.status === 'connected') {
        FB.logout(function (_response: any) {});
      }
    });
  }

  async getPages(accessToken: string): Promise<[any] | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        '/me/accounts?fields=id%2Cname%2Ccategory_list%2Cinstagram_business_account{id,name,username,profile_picture_url,follows_count,followers_count,media_count}%2Caccess_token',
        'GET',
        { access_token: accessToken },
        function (accountsResponse: any) {
          if (accountsResponse && accountsResponse.data.length > 0) {
            resolve(accountsResponse.data);
          } else {
            reject(new Error('No accounts found'));
          }
        }
      );
    });
  }

  async facebookLogin(configuration?: number): Promise<any> {
    let configuration_id = configuration ?? this.instagramConfigId;
    let type_resp = configuration != null ? 'token' : 'code';
    return new Promise((resolve, reject) => {
      FB.login(
        async (response: any) => {
          if (response) {
            resolve(response);
          } else {
            reject(new Error('Facebook login failed'));
          }
        },
        {
          config_id: configuration_id, //781973270459738 id
          // config_id: 379742928129101,
          response_type: type_resp,
          override_default_response_type: true,
        }
      );
    });
  }

  async subscribePage(pageId: string, pageAccessToken: string): Promise<boolean | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${pageId}/subscribed_apps`,
        'POST',
        {
          object: 'instagram',
          subscribed_fields: 'messages',
          access_token: pageAccessToken,
        },
        function (response: any) {
          if (response.success) {
            resolve(response.success);
          } else {
            reject(new Error('Failed to subscribe page'));
          }
        }
      );
    });
  }

  async getLoginStatus(): Promise<any> {
    return new Promise((resolve, _reject) => {
      FB.getLoginStatus((authResponse: any) => {
        if (authResponse.authResponse !== null) {
          resolve(authResponse);
        } else {
          resolve(false);
        }
      });
    });
  }

  async getPage(page_id: string, accessToken: string): Promise<PageResponse | undefined> {
    return new Promise<PageResponse | undefined>((resolve, _reject) => {
      FB.api(
        `/${page_id}`,
        'GET',
        {
          fields:
            'id,name,instagram_business_account{id,name,username,profile_picture_url,follows_count,followers_count,media_count}',
          access_token: accessToken,
        },
        function (response: any) {
          if (response) {
            resolve(response);
          } else {
            resolve(undefined);
          }
        }
      );
    });
  }

  async getUserData(accessToken: string): Promise<any> {
    return new Promise((resolve, reject) => {
      FB.api('/me', 'GET', { access_token: accessToken }, function (userDataResponse: any) {
        if (userDataResponse) {
          resolve(userDataResponse);
        } else {
          reject(new Error('Failed to get user data'));
        }
      });
    });
  }

  async revokePermissions(userId: string, accessToken: string): Promise<unknown> {
    return new Promise((resolve, reject) => {
      FB.api(
        `/${userId}/permissions`,
        'DELETE',
        { access_token: accessToken },
        function (revokeResponse: any) {
          if (revokeResponse) {
            resolve(revokeResponse);
          } else {
            reject(new Error('Failed to revoke permissions'));
          }
        }
      );
    });
  }

  async generateAccessToken(code: string): Promise<string | undefined> {
    return new Promise((resolve, reject) => {
      FB.api(
        '/oauth/access_token',
        'GET',
        {
          client_id: environment.facebookAppId,
          client_secret: environment.facebookAppSecret,
          code: code,
        },
        function (authResponse: any) {
          if (authResponse.access_token) {
            resolve(authResponse.access_token);
          } else {
            reject(new Error('Failed to generate access token'));
          }
        }
      );
    });
  }

  switchChanged(event: any) {
    if (!this.bot?.id) {
      return;
    }

    const previousValue = this.botEnabled;
    this.botEnabled = event.checked; // Actualizar inmediatamente

    this.instagramBotService
      .updateAccountSubscription(this.bot.id, { is_enabled: event.checked })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (_response) => {
          this.getInstagramBot(); // Recargar datos como lo hace Messenger
          this.ms.add({
            key: 'tst',
            severity: 'success',
            summary: 'Acción exitosa',
            detail: `Instagram Bot ${event.checked ? 'habilitado' : 'deshabilitado'}!`,
          });
        },
        error: (_err) => {
          this.botEnabled = previousValue; // Revertir en caso de error
          this.ms.add({
            key: 'tst',
            severity: 'error',
            summary: 'Acción fallida',
            detail: 'Hubo un problema al actualizar el estado del bot de Instagram.',
          });
        },
      });
  }

  commentsReplySwitchChanged(event: { checked: boolean }) {
    if (event.checked) {
      this.saveCommentsEnabled();
    } else {
      this.saveCommentsDisabled();
    }
  }

  private saveCommentsEnabled(): void {
    if (!this.canStartCommentsSave()) {
      this.commentsReplyEnabled = false;
      return;
    }
    const previousValue = false;
    this.savingCommentsConfig = true;
    this.instagramBotService
      .updateAccountSubscription(this.bot!.id, {
        comments_reply_enabled: true,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingCommentsConfig = false;
          this.getInstagramBot();
          this.showMessage(
            'success',
            'Comentarios activados',
            'Configura el mensaje y pulsa «Guardar cambios» para completar la automatización.'
          );
        },
        error: (err: unknown) => {
          this.commentsReplyEnabled = previousValue;
          this.handleSaveCommentsConfigError(err);
        },
      });
  }

  private saveCommentsDisabled(): void {
    if (!this.canStartCommentsSave()) {
      this.commentsReplyEnabled = true;
      return;
    }
    const previousValue = this.commentsReplyEnabled;
    this.savingCommentsConfig = true;
    this.instagramBotService
      .updateAccountSubscription(this.bot!.id, {
        comments_reply_enabled: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingCommentsConfig = false;
          this.getInstagramBot();
          this.showMessage(
            'success',
            'Acción exitosa',
            'Respuesta automática de comentarios desactivada'
          );
        },
        error: (err: unknown) => {
          this.commentsReplyEnabled = previousValue;
          this.handleSaveCommentsConfigError(err);
        },
      });
  }

  private canStartCommentsSave(): boolean {
    if (!this.bot?.id) {
      return false;
    }
    if (!this.instagramBot) {
      this.showMessage(
        'warn',
        'Instagram aún no está vinculado',
        'Primero completa los pasos de arriba: conecta Facebook, detecta la cuenta de Instagram y confirma la vinculación. Cuando aparezca tu usuario de Instagram, ya podrás guardar la configuración de comentarios.'
      );
      return false;
    }
    return true;
  }

  private isCommentsReplyFormValid(): boolean {
    if (!this.commentsReplyEnabled) {
      return true;
    }
    if (!this.commentsReplySendDm && !this.commentsReplySendPublic) {
      this.showMessage(
        'error',
        'Revisa la configuración',
        'Activa al menos una acción: mensaje directo o respuesta pública.'
      );
      return false;
    }
    if (this.commentsReplySendDm && !(this.commentsPredefinedMessage ?? '').trim()) {
      this.showMessage(
        'error',
        'Revisa la configuración',
        'El texto del mensaje directo no puede estar vacío.'
      );
      return false;
    }
    if (this.commentsReplySendPublic && !(this.commentsPredefinedMessagePublic ?? '').trim()) {
      this.showMessage(
        'error',
        'Revisa la configuración',
        'El texto de la respuesta pública no puede estar vacío.'
      );
      return false;
    }
    return true;
  }

  private handleSaveCommentsConfigError(err: unknown): void {
    this.savingCommentsConfig = false;
    let detail: string | undefined;
    if (err instanceof HttpErrorResponse) {
      const body = err.error;
      if (body && typeof body === 'object' && 'message' in body) {
        detail = String((body as { message: unknown }).message);
      } else if (typeof body === 'string' && body.length > 0) {
        detail = body;
      } else if (err.statusText) {
        detail = `${err.status} ${err.statusText}`;
      }
    }
    this.showMessage(
      'error',
      'Acción fallida',
      detail ?? 'No se pudo guardar la configuración de comentarios'
    );
    this.getInstagramBot();
  }

  saveCommentsConfiguration() {
    if (!this.canStartCommentsSave() || !this.isCommentsReplyFormValid()) {
      return;
    }

    this.savingCommentsConfig = true;
    this.instagramBotService
      .updateAccountSubscription(this.bot!.id, {
        comments_reply_enabled: this.commentsReplyEnabled,
        comments_reply_mode: false,
        comments_predefined_message: this.commentsPredefinedMessage?.trim() || null,
        comments_trigger_type: this.commentsTriggerType,
        comments_trigger_keywords: this.commentsKeywordsArray,
        comments_reply_send_dm: this.commentsReplySendDm,
        comments_reply_send_public: this.commentsReplySendPublic,
        comments_predefined_message_public: this.commentsReplySendPublic
          ? this.commentsPredefinedMessagePublic.trim()
          : null,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.savingCommentsConfig = false;
          this.getInstagramBot();
          this.showMessage('success', 'Acción exitosa', 'Configuración de comentarios actualizada');
        },
        error: (err: unknown) => this.handleSaveCommentsConfigError(err),
      });
  }
}
